/** Virtual Production Office — the "Office Floor".
 *
 *  Master container that:
 *    1. Fetches the RBAC access matrix (Executive Master Keys +
 *       per-cubicle dept crew).
 *    2. Renders the 4 department tabs — Locations, Auditions,
 *       Costumes, Sets — greying-out and disabling any cubicle the
 *       caller is not entitled to enter.
 *    3. Loads the department-specific cubicle component (rich UI
 *       lives in dashboards/cubicles/*).
 *    4. Anchors the shared WebSocket chat pane + one-click Jitsi
 *       meeting launcher at the TOP of each active cubicle — access
 *       is already scoped server-side.
 *    5. Renders the shared Rich Media Vault beneath (asset gallery).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import {
  MapPin, Video, Shirt, Palette, Plus, Trash2, Send,
  MessageCircle, ArrowLeft, PlayCircle, X, Shield, Lock, DoorClosed, DoorOpen, Bell,
  Sparkles, Umbrella, Gavel, UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useWebSocket, subscribeWs, unsubscribeWs } from "@/hooks/useWebSocket";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import CastingCubicle   from "@/dashboards/cubicles/CastingCubicle";
import LocationsCubicle from "@/dashboards/cubicles/LocationsCubicle";
import CostumesCubicle  from "@/dashboards/cubicles/CostumesCubicle";
import SetsCubicle      from "@/dashboards/cubicles/SetsCubicle";
import CubicleSubInvitePanel from "@/components/CubicleSubInvitePanel";
import OfficeFloor from "@/components/OfficeFloor";
import CubicleMediaVault from "@/components/CubicleMediaVault";
import CubicleChat from "@/components/CubicleChat";
import HuddlePanel from "@/components/HuddlePanel";

const CUBICLES = [
  { key: "locations", label: "Locations", Icon: MapPin,   accent: "#4EC1E4", Body: LocationsCubicle },
  { key: "auditions", label: "Auditions", Icon: Video,    accent: "#F5B82E", Body: CastingCubicle   },
  { key: "costumes",  label: "Costumes",  Icon: Shirt,    accent: "#FF4FA3", Body: CostumesCubicle  },
  { key: "sets",      label: "Sets",      Icon: Palette,  accent: "#B981F5", Body: SetsCubicle      },
  { key: "insurance", label: "Insurance", Icon: Umbrella, accent: "#22D3EE", Body: null             },
  { key: "legal",     label: "Legal",     Icon: Gavel,    accent: "#818CF8", Body: null             },
];

const GENERIC_ACCENT = "#4FD1C5";

/** Build a cubicle descriptor for any office-floor key.
 *  If the key has a rich dedicated component we return that entry.
 *  Otherwise, we synthesize a lightweight descriptor pulled from the
 *  /office-floor/catalog metadata so the CubicleFrame still renders
 *  the shared chat + sub-invite + media vault stack.
 */
function resolveCubicle(key, catalogEntry) {
  const rich = CUBICLES.find((c) => c.key === key);
  if (rich) return rich;
  if (!key) return null;
  return {
    key,
    label: catalogEntry?.label || key.replace(/_/g, " "),
    Icon:  MapPin,   // neutral icon — pod-specific icon rendered inside body
    accent: catalogEntry?.pod ? GENERIC_ACCENT : GENERIC_ACCENT,
    Body:  null,     // no rich body — CubicleFrame skips <Body/> gracefully
  };
}

const JITSI_DOMAIN = "meet.jit.si";
const EXT_API_SRC = "https://meet.jit.si/external_api.js";

function loadJitsi() {
  return new Promise((res, rej) => {
    if (window.JitsiMeetExternalAPI) return res(window.JitsiMeetExternalAPI);
    let s = document.querySelector(`script[src="${EXT_API_SRC}"]`);
    if (!s) {
      s = document.createElement("script"); s.src = EXT_API_SRC; s.async = true;
      document.body.appendChild(s);
    }
    s.addEventListener("load", () => res(window.JitsiMeetExternalAPI));
    s.addEventListener("error", rej);
  });
}


export default function ProjectCubicles() {
  const { id: projectId } = useParams();
  const [project, setProject] = useState(null);
  const [access, setAccess] = useState(null);   // {master_key, master_role, cubicle_access}
  const [catalog, setCatalog] = useState(null); // /office-floor/catalog
  const [active, setActive] = useState("locations");

  // Query params — sub-members ('scope=cubicle_only') and cubicle deep-links.
  // Uses useLocation so ?cubicle= changes trigger a re-render (previously
  // the URL updated but React never resynced state — clicking an office-
  // floor tile appeared to no-op).
  const location = useLocation();
  const urlParams = useMemo(() => new URLSearchParams(location.search),
                            [location.search]);
  const scopeParam   = urlParams.get("scope");           // "cubicle_only" | null
  const cubicleParam = urlParams.get("cubicle");         // e.g. "camera"
  const isCubicleScoped = scopeParam === "cubicle_only";
  const cubicleAnchorRef = useRef(null);

  useEffect(() => {
    api.get(`/projects/${projectId}`).then(({ data }) => setProject(data.project))
       .catch(() => setProject(null));
    api.get("/office-floor/catalog").then(({ data }) => setCatalog(data))
       .catch(() => setCatalog(null));
    api.get(`/projects/${projectId}/office-floor/access`)
       .then(({ data }) => {
         setAccess(data);
         // Deep-link resolution:
         //  • ?cubicle=<key> present AND granted → snap to it. Now
         //    accepts any of the 60+ office-floor keys (was previously
         //    limited to the 4 legacy tabs).
         //  • ?cubicle=<key> present + DENIED  → still set active=<key>
         //    so CubicleFrame renders the 'Cubicle Locked' card.
         //  • no deep-link → first accessible legacy tab.
         if (cubicleParam) {
           setActive(cubicleParam);
         } else {
           const firstOK = CUBICLES.find((c) => data.cubicle_access?.[c.key]);
           if (firstOK) setActive(firstOK.key);
         }
       })
       .catch(() => setAccess({ master_key: false, master_role: "",
                                cubicle_access: {} }));
  }, [projectId]);

  // Sync `active` whenever ?cubicle= changes (e.g. after clicking a tile
  // in the OfficeFloor grid). The initial useEffect above depends only
  // on projectId, so client-side query param changes need this dedicated
  // sync — otherwise tiles look dead.
  useEffect(() => {
    if (cubicleParam) setActive(cubicleParam);
  }, [cubicleParam]);

  // When the user clicks a tile in the OfficeFloor grid (which is
  // rendered BELOW the CubicleFrame), we send them back up so the
  // cubicle they just picked lands in view. `window.scrollTo(0, 0)`
  // is used instead of scrollIntoView so we land on the page header
  // + tabs first (not deep into the sub-invite panel — the user
  // complained the earlier behavior "jumped to adding crew").
  useEffect(() => {
    if (!cubicleParam) return;
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }, [cubicleParam]);

  const catalogEntry = useMemo(
    () => (catalog?.cubicles || []).find((c) => c.key === active),
    [catalog, active],
  );
  const cubicle = useMemo(
    () => resolveCubicle(active, catalogEntry),
    [active, catalogEntry],
  );
  const canOpenActive = !!access?.cubicle_access?.[active];

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8"
         data-testid="cubicles-page">
      {access?.master_key ? (
        <Link to="/projects"
              className="text-[10px] tracking-[0.28em] uppercase text-white/45 hover:text-white inline-flex items-center gap-1"
              data-testid="back-to-projects-link">
          <ArrowLeft size={11}/> Back to projects
        </Link>
      ) : (
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/35 inline-flex items-center gap-1"
             data-testid="restricted-crew-note"
             title="Restricted to this project — contact a producer / line producer / main AD to leave.">
          <Lock size={11}/> Project-scoped access
        </div>
      )}
      <div className="flex items-baseline gap-3 mt-3 flex-wrap">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
          Virtual Production Office
        </div>
        {project?.type_label && (
          <div className="text-[10px] tracking-[0.22em] uppercase text-emerald-300">
            {project.type_label}
          </div>
        )}
        {access?.master_key && (
          <div className="text-[10px] tracking-[0.22em] uppercase text-amber-300 inline-flex items-center gap-1"
               data-testid="master-key-badge">
            <Shield size={11}/> Executive Master Key
            {access.master_role && access.master_role !== "owner" && (
              <span className="text-white/45">· {access.master_role.replace("_", " ")}</span>
            )}
          </div>
        )}
      </div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-1">
        {project?.title || "Project"}
      </h1>

      <div className="mt-3 flex flex-wrap gap-2">
        <Link to={`/p/${projectId}/continuity`}
              className="text-[10px] tracking-[0.22em] uppercase text-white/70 hover:text-white border border-white/15 hover:border-white/35 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
              data-testid="cubicles-open-continuity">
          <Sparkles size={11}/> Continuity Book
        </Link>
      </div>

      {isCubicleScoped && (
        <div className="mt-4 flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-white/70 border border-white/12 rounded-full px-3 py-1.5 inline-flex"
             data-testid="cubicle-scoped-badge">
          <Lock size={11}/> Sub-invitee · {cubicleParam || active} cubicle only
        </div>
      )}

      {/* Office floor tabs — legacy 4 rich cubicles. Hidden for cubicle-
          scoped sub-invitees. When the active cubicle isn't one of the
          legacy tabs (e.g. Camera, Gaffer, Kids Coordinator), we surface
          a subtle "Now inside" pill so the user always knows where they
          are — the shared CubicleFrame beneath keeps chat + sub-invite
          + media vault working uniformly for every one of the 60+ keys. */}
      {!isCubicleScoped && (
      <div className="mt-6 flex gap-2 flex-wrap border-b border-white/10 items-center"
           data-testid="cubicle-tabs">
        {CUBICLES.map((c) => {
          const unlocked = !!access?.cubicle_access?.[c.key];
          const isActive = c.key === active;
          return (
            <button key={c.key}
                    type="button"
                    onClick={() => unlocked && setActive(c.key)}
                    disabled={!unlocked}
                    className={
                      "px-4 py-3 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2 border-b-2 -mb-px transition-colors " +
                      (isActive
                        ? "border-current"
                        : "border-transparent hover:text-white/80") +
                      (!unlocked ? " opacity-40 cursor-not-allowed" : "") +
                      (isActive || unlocked ? " text-white" : " text-white/40")
                    }
                    style={isActive ? { color: c.accent, borderColor: c.accent } : {}}
                    data-testid={`cubicle-tab-${c.key}`}>
              <c.Icon size={13}/>{c.label}
              {!unlocked && <Lock size={10}/>}
            </button>
          );
        })}
        {!CUBICLES.find((c) => c.key === active) && cubicle && (
          <div className="ml-auto text-[10px] tracking-[0.22em] uppercase text-white/60 inline-flex items-center gap-1.5 pb-3"
               data-testid={`cubicle-nonlegacy-badge-${active}`}
               title="This cubicle uses the shared team workspace (chat + invites + media). Rich cubicle body coming soon.">
            Now inside · <span className="text-white">{cubicle.label}</span>
          </div>
        )}
      </div>
      )}

      <div ref={cubicleAnchorRef} className="scroll-mt-6">
        {cubicle && (canOpenActive ? (
          <CubicleFrame key={cubicle.key}
                        projectId={projectId}
                        project={project}
                        cubicle={cubicle}
                        access={access}/>
        ) : (
          <div className="mt-8 cs-card rounded-2xl p-10 text-center">
            <Lock size={28} className="mx-auto text-white/45"/>
            <div className="font-display text-xl uppercase tracking-tight mt-3">
              Cubicle Locked
            </div>
            <div className="text-sm text-white/55 mt-1">
              Only Executive Master Keys and the {cubicle.label.toLowerCase()} department
              crew can enter this cubicle.
            </div>
          </div>
        ))}
      </div>

      {/* Secondary navigation — the full 63-cubicle office floor + the
          cross-department huddle controls. Moved BELOW the primary
          cubicle content so the user's eye lands on chat + invite +
          media first, then finds the way to switch cubicles. */}
      {!isCubicleScoped && access && (
        <div className="mt-14 pt-8 border-t border-white/8">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/45">
            Office floor · switch cubicles
          </div>
          <p className="mt-1 text-xs text-white/50">
            Every department gets a private cubicle. Click any unlocked
            tile below to walk in — chat, invites and reference media
            are inside each one.
          </p>
          <OfficeFloor projectId={projectId} access={access}/>
        </div>
      )}

      {/* Iter-93d — Cross-department Huddles. Producer / master calls
          a temporary huddle (2 hours default) so costume + action +
          camera can walk into each other's cubicles for a stunt scene.
          Positioned last as it is a scheduling tool, not the primary
          invite flow. */}
      {!isCubicleScoped && access && access?.master_key && (
        <div className="mt-10 pt-6 border-t border-white/8"
             data-testid="huddle-panel-section">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/45">
            Cross-department huddles · advanced
          </div>
          <p className="mt-1 text-xs text-white/50">
            Temporarily let two or more cubicles into each other for a
            scene (e.g. costume + action + camera during a stunt).
            Access reverts automatically when the timer ends.
          </p>
          <HuddlePanel
            projectId={projectId}
            canManage={!!access?.master_key}
            availableCubicles={(catalog?.cubicles || []).filter((c) =>
              access?.cubicle_access?.[c.key]
            )}
          />
        </div>
      )}
    </div>
  );
}


/** Frame around a specific cubicle body — anchors the WS chat + Jitsi
 *  meeting comms at the top; renders the rich cubicle-specific body
 *  under; keeps the shared Rich Media Vault at the bottom. */
function CubicleFrame({ projectId, project, cubicle, access }) {
  const { user } = useAuth();
  const [thread, setThread]     = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft]       = useState("");
  const [meeting, setMeeting]   = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [media, setMedia]       = useState([]);
  const [showAdd, setShowAdd]   = useState(false);
  const [addForm, setAddForm]   = useState({ kind: "image", url: "", caption: "" });
  // Cubicle door lock state — separate load so we can render the door
  // UI even for callers whose RBAC would block the cubicle body.
  const [lock, setLock]         = useState(null);
  const [knockPending, setKnockPending] = useState(false);

  const isMaster = !!access?.master_key;
  const canOverride = isMaster;
  const iAmOccupant = lock?.locked && lock.locked_by === user?.id;
  const doorClosed = !!lock?.locked && !iAmOccupant && !canOverride;

  const loadLock = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/projects/${projectId}/cubicles/${cubicle.key}/privacy`,
      );
      setLock(data);
    } catch { /* silent */ }
  }, [projectId, cubicle.key]);

  const loadAll = useCallback(async () => {
    try {
      const [t, m] = await Promise.all([
        api.post(`/projects/${projectId}/cubicles/${cubicle.key}/thread`),
        api.get(`/projects/${projectId}/cubicles/${cubicle.key}/media`),
      ]);
      setThread(t.data.thread);
      setMedia(m.data.items || []);
    } catch (e) {
      console.warn("[cubicle frame]", e?.message);
    }
  }, [projectId, cubicle.key]);

  const loadMessages = useCallback(async (tid) => {
    if (!tid) return;
    try {
      const { data } = await api.get(`/messaging/threads/${tid}/messages`, { params: { limit: 200 } });
      setMessages(data.messages || []);
      api.post(`/messaging/threads/${tid}/read`).catch(() => {});
    } catch { /* silent */ }
  }, []);

  useEffect(() => { void loadLock(); }, [loadLock]);
  useEffect(() => {
    if (!doorClosed) void loadAll();
  }, [loadAll, doorClosed]);
  useEffect(() => { if (thread?.id) void loadMessages(thread.id); }, [thread?.id, loadMessages]);
  useEffect(() => {
    const ch = `cubicle:${projectId}:${cubicle.key}`;
    subscribeWs([ch]);
    return () => { unsubscribeWs([ch]); };
  }, [projectId, cubicle.key]);

  useWebSocket((evt) => {
    if (!evt?.type) return;
    if (evt.type === "cubicle.locked" || evt.type === "cubicle.unlocked") {
      void loadLock();
      return;
    }
    if (evt.type === "cubicle.knock_answered") {
      if (evt.decision === "accept") toast.success("Your knock was accepted — walking in.");
      else toast("Your knock was declined.", { icon: "🚪" });
      setKnockPending(false);
      void loadLock();
      return;
    }
    if (evt.type === "cubicle.knock" && iAmOccupant) {
      const k = evt.knock;
      toast(`${k.knocker_name} is knocking on ${cubicle.label}`, {
        description: "Open the door via the header buttons.",
        icon: "🚪",
      });
      return;
    }
    if (evt.type === "message.new" && thread?.id === evt.thread_id) {
      setMessages((prev) => [...prev, evt.message]);
      api.post(`/messaging/threads/${thread.id}/read`).catch(() => {});
    }
    if (evt.type === "cubicle.media_added" || evt.type === "cubicle.media_removed") {
      void loadAll();
    }
    if (evt.type === "cubicle.meeting_live") {
      setMeeting({ jitsi_room: evt.jitsi_room, jitsi_domain: evt.jitsi_domain });
    }
  });

  const flipLock = async () => {
    try {
      if (lock?.locked && (iAmOccupant || canOverride)) {
        await api.post(`/projects/${projectId}/cubicles/${cubicle.key}/unlock`);
      } else {
        await api.post(`/projects/${projectId}/cubicles/${cubicle.key}/lock`);
      }
      await loadLock();
    } catch (e) { toast.error(e?.response?.data?.detail || "Lock failed"); }
  };

  const knock = async () => {
    try {
      await api.post(`/projects/${projectId}/cubicles/${cubicle.key}/knock`);
      setKnockPending(true);
      toast.success("Knock sent — waiting for the occupant.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Knock failed"); }
  };

  const send = async () => {
    const body = draft.trim();
    if (!body || !thread?.id) return;
    setDraft("");
    try { await api.post(`/messaging/threads/${thread.id}/messages`, { body }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  const startMeeting = async () => {
    try {
      const { data } = await api.post(
        `/projects/${projectId}/cubicles/${cubicle.key}/meeting/start`,
      );
      setMeeting({ jitsi_room: data.jitsi_room, jitsi_domain: data.jitsi_domain });
      setVideoOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Meeting failed"); }
  };

  const addMedia = async () => {
    if (!addForm.url.trim()) return toast.error("URL is required");
    try {
      await api.post(`/projects/${projectId}/cubicles/${cubicle.key}/media`, {
        kind: addForm.kind, url: addForm.url.trim(),
        caption: addForm.caption.trim(),
      });
      setShowAdd(false);
      setAddForm({ kind: "image", url: "", caption: "" });
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };

  const removeMedia = async (mid) => {
    if (!window.confirm("Remove this media entry?")) return;
    try {
      await api.delete(`/projects/${projectId}/cubicles/${cubicle.key}/media/${mid}`);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const CIcon = cubicle.Icon;
  const Body = cubicle.Body;

  // ── Closed-door mode ─────────────────────────────────────────────
  // If the cubicle is privately locked by someone else AND the caller
  // is not a Master Key holder, we render the "Front / Back / Knock"
  // door instead of the cubicle content.
  if (doorClosed) {
    const occupant = lock?.occupant?.name || "Someone";
    const roleTag  = lock?.occupant?.primary_role
      ? ` · ${lock.occupant.primary_role.replace(/_/g, " ")}` : "";
    return (
      <div className="mt-8 cs-card rounded-2xl p-10 text-center"
           data-testid={`cubicle-door-${cubicle.key}`}>
        <DoorClosed size={38} className="mx-auto text-white/55"/>
        <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300 mt-3">
          Do Not Disturb
        </div>
        <div className="font-display text-2xl uppercase tracking-tight mt-1">
          {cubicle.label} · Locked
        </div>
        <div className="text-sm text-white/60 mt-2">
          {occupant}{roleTag} is currently using this cubicle privately.
        </div>
        {lock?.reason && (
          <div className="mt-3 text-xs text-white/50 italic">&ldquo;{lock.reason}&rdquo;</div>
        )}
        <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
          <Button variant="ghost"
                  className="h-10 gap-2 border border-white/15 text-white/80"
                  onClick={() => window.history.back()}
                  data-testid={`cubicle-door-back-${cubicle.key}`}>
            <ArrowLeft size={14}/> Back
          </Button>
          <Button variant="ghost"
                  className="h-10 gap-2 border border-white/15 text-white/80"
                  onClick={() => { void loadLock(); }}
                  data-testid={`cubicle-door-front-${cubicle.key}`}
                  title="Refresh — check who's inside">
            <DoorClosed size={14}/> Front
          </Button>
          <Button className="h-10 gap-2 bg-amber-300 text-black hover:bg-amber-200"
                  onClick={knock}
                  disabled={knockPending}
                  data-testid={`cubicle-door-knock-${cubicle.key}`}>
            <Bell size={14}/> {knockPending ? "Knocked — waiting…" : "Knock"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-6"
         data-testid={`cubicle-pane-${cubicle.key}`}>
      {/* Comms bar — chat + start-meeting anchored at the top */}
      <div className="cs-card rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap"
           style={{ borderColor: cubicle.accent + "44" }}
           data-testid={`cubicle-comms-${cubicle.key}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
               style={{ background: cubicle.accent + "20", color: cubicle.accent }}>
            <CIcon size={18}/>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
              Cubicle · {cubicle.label}
            </div>
            <div className="text-sm text-white/70">
              {media.length} media · {messages.length} messages
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="ghost"
                  className="h-9 gap-2 text-white/80 hover:text-white"
                  onClick={() => document.getElementById(`chat-${cubicle.key}`)?.scrollIntoView({ behavior: "smooth" })}
                  data-testid={`cubicle-chat-focus-${cubicle.key}`}>
            <MessageCircle size={14}/> Chat
          </Button>
          <Button variant="ghost"
                  className="h-9 gap-2 text-white/80 hover:text-white"
                  onClick={() => document.querySelector(`[data-testid="sub-invite-panel-${cubicle.key}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  data-testid={`cubicle-invite-focus-${cubicle.key}`}
                  title="Jump to this cubicle's crew list — invite, chat, or hire from there">
            <UserPlus size={14}/> Crew
          </Button>
          <Button variant="ghost"
                  className="h-9 gap-2 text-white/80 hover:text-white"
                  onClick={flipLock}
                  data-testid={`cubicle-lock-toggle-${cubicle.key}`}
                  title={lock?.locked
                    ? "Cubicle is private — unlock the door"
                    : "Flip to Do Not Disturb (lock the cubicle)"}>
            {lock?.locked
              ? <><DoorClosed size={14}/> Locked</>
              : <><DoorOpen size={14}/> Open</>}
          </Button>
          <Button className="h-9 gap-2"
                  style={{ background: cubicle.accent, color: "#0b0b0b" }}
                  onClick={startMeeting}
                  data-testid={`cubicle-start-meeting-${cubicle.key}`}>
            <Video size={14}/> Start meeting
          </Button>
          {meeting?.jitsi_room && (
            <Button variant="ghost"
                    className="h-9 gap-2 text-emerald-300 hover:text-emerald-200"
                    onClick={() => setVideoOpen(true)}
                    data-testid={`cubicle-join-meeting-${cubicle.key}`}>
              <PlayCircle size={14}/> Join live
            </Button>
          )}
        </div>
      </div>

      {/* Rich, department-specific cubicle body — only the 4 legacy
          cubicles (locations/auditions/costumes/sets) ship a dedicated
          component. For the other 58 office-floor keys the frame still
          renders chat + sub-invite + media vault, which is what most
          departments actually need day-1. */}
      {Body && <Body projectId={projectId}/>}

      {/* Phase-27b · iter-93c — Tri-thread chat (team + production + private)
          moved to the TOP of the cubicle ecosystem so the first thing
          the user sees is the room's ongoing conversation. */}
      <CubicleChat projectId={projectId}
                   cubicleKey={cubicle.key}
                   canInviteGroup={!!access?.master_key}/>

      {/* Sub-invite panel — anyone in this cubicle can invite subordinates.
          Each row exposes a 1-on-1 Chat button so a producer / HOD can
          DM a specific crew member without leaving the cubicle. */}
      <CubicleSubInvitePanel projectId={projectId}
                             cubicleKey={cubicle.key}
                             cubicleLabel={cubicle.label}
                             accent={cubicle.accent}
                             canHire={!!access?.master_key}/>

      {/* Rich Media Vault */}
      <div className="cs-card rounded-2xl p-5"
           data-testid={`cubicle-gallery-${cubicle.key}`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
              Rich Media Vault
            </div>
            <div className="font-display text-lg uppercase tracking-tight">
              {media.length} item(s)
            </div>
          </div>
          <Button onClick={() => setShowAdd(true)}
                  className="h-9 gap-2 bg-white text-black hover:bg-white/90"
                  data-testid={`cubicle-add-media-${cubicle.key}`}>
            <Plus size={14}/> Add media
          </Button>
        </div>
        {media.length === 0 ? (
          <div className="text-sm text-white/50 py-10 text-center">
            No shared reference media yet — drop panorama shots or clips.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {media.map((m) => (
              <div key={m.id}
                   className="rounded-xl border border-white/10 bg-black/40 overflow-hidden group relative"
                   data-testid={`media-card-${m.id}`}>
                <div className="aspect-video bg-black flex items-center justify-center">
                  {m.kind === "image" ? (
                    <img src={m.url} alt={m.caption || ""}
                         className="w-full h-full object-cover"
                         onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                  ) : (
                    <video src={m.url} controls
                           poster={m.thumbnail_url || undefined}
                           className="w-full h-full"/>
                  )}
                </div>
                <div className="p-2.5">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-white/45">
                    {m.kind} · {m.uploader_name}
                  </div>
                  {m.caption && (
                    <div className="text-xs text-white/80 mt-1 line-clamp-2">{m.caption}</div>
                  )}
                </div>
                <button type="button"
                        onClick={() => removeMedia(m.id)}
                        className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/70 text-white/80 hover:text-rose-300 hidden group-hover:flex items-center justify-center"
                        data-testid={`media-remove-${m.id}`}>
                  <Trash2 size={13}/>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Phase-27b — Shared Media Vault with approval workflow */}
      <CubicleMediaVault projectId={projectId}
                         cubicleKey={cubicle.key}
                         canApprove={!!access?.master_key}
                         accent={cubicle.accent}/>

      {/* Add media dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg bg-neutral-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-tight">
              Add {cubicle.label} Media
            </DialogTitle>
            <DialogDescription className="sr-only">
              Add an image or video reference to this cubicle&apos;s vault.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Kind</Label>
              <select value={addForm.kind}
                      onChange={(e) => setAddForm({ ...addForm, kind: e.target.value })}
                      className="mt-1 w-full h-10 rounded-md bg-black/40 border border-white/15 px-3 text-sm"
                      data-testid="media-kind">
                <option value="image">Image</option>
                <option value="video">Video</option>
              </select></div>
            <div><Label>URL</Label>
              <Input value={addForm.url}
                     onChange={(e) => setAddForm({ ...addForm, url: e.target.value })}
                     placeholder={addForm.kind === "image" ? "https://…/photo.jpg" : "https://…/clip.mp4"}
                     className="mt-1 bg-black/40 border-white/15"
                     data-testid="media-url"/></div>
            <div><Label>Caption</Label>
              <Input value={addForm.caption}
                     onChange={(e) => setAddForm({ ...addForm, caption: e.target.value })}
                     placeholder="Optional caption"
                     className="mt-1 bg-black/40 border-white/15"
                     data-testid="media-caption"/></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={addMedia}
                    className="bg-white text-black hover:bg-white/90"
                    data-testid="media-submit">Add to vault</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Jitsi overlay */}
      {videoOpen && meeting?.jitsi_room && (
        <JitsiOverlay meeting={meeting}
                      title={`${cubicle.label} · ${project?.title || "Cubicle"}`}
                      displayName={user?.name || user?.email || "Crew"}
                      onClose={() => setVideoOpen(false)}/>
      )}
    </div>
  );
}


function JitsiOverlay({ meeting, title, displayName, onClose }) {
  const containerRef = useRef(null);
  useEffect(() => {
    let api;
    loadJitsi().then((Api) => {
      if (!containerRef.current) return;
      api = new Api(meeting.jitsi_domain || JITSI_DOMAIN, {
        roomName: meeting.jitsi_room,
        parentNode: containerRef.current,
        width: "100%",
        height: "100%",
        userInfo: { displayName },
        configOverwrite: { prejoinPageEnabled: false, disableDeepLinking: true },
        interfaceConfigOverwrite: { MOBILE_APP_PROMO: false, SHOW_JITSI_WATERMARK: false },
      });
      api.addEventListener("readyToClose", () => onClose?.());
    });
    return () => { try { api?.dispose(); } catch { /* noop */ } };
  }, [meeting.jitsi_room, meeting.jitsi_domain, displayName, onClose]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex flex-col"
         data-testid="cubicle-jitsi-modal">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-emerald-300">Cubicle · Live</div>
          <div className="font-display text-lg">{title}</div>
        </div>
        <Button variant="ghost" onClick={onClose}
                className="h-9 gap-2 text-white/75"
                data-testid="cubicle-jitsi-close">
          <X size={14}/> Leave
        </Button>
      </div>
      <div ref={containerRef} className="flex-1 min-h-0 bg-black"/>
    </div>
  );
}
